import { useState, type ReactNode } from "react";
import type { QuizQuestion } from "../scenario/types";
import { renderInlineMarkdown } from "../lib/markdown";
import "./results-compact.css";
import { tx, txr } from "../lib/tx";

type Dim = { dimension: string; label: string; percent: number };
type Ev = { id: string; at: number; kind: string; label: string };

/**
 * End screen of every 3D course (rendered by Debrief.tsx after the quiz).
 * Results = one glass panel: score ring + verdict, a table with one row per
 * question (wrong rows highlighted, the first wrong one open), the exposure
 * bars (or the risk-meter review for courses that use it), then
 * "Finish course" -> a thank-you page that confirms the course is complete,
 * with a restart option.
 */
export function ResultsCompact({
  title,
  quiz,
  answers,
  passed,
  correct,
  dims,
  evidence,
  choices,
}: {
  title: string;
  quiz: QuizQuestion[];
  answers: Record<string, string>;
  passed: boolean;
  correct: number;
  dims: Dim[] | null;
  evidence: Ev[];
  /** risk-meter courses: the walkthrough list of the learner's choices. */
  choices?: { count: number; body: ReactNode } | null;
}) {
  const rows = quiz.map((q, i) => {
    const chosen = q.options.find((o) => o.id === answers[q.id]);
    return { q, i, chosen, ok: !!chosen?.correct, right: q.options.find((o) => o.correct) };
  });
  const firstWrong = rows.find((r) => !r.ok)?.q.id ?? null;
  const [open, setOpen] = useState<string | null>(firstWrong);
  const [showEv, setShowEv] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [done, setDone] = useState(false);
  const total = quiz.length;
  const pct = total ? Math.round((correct / total) * 100) : 100;

  if (done) {
    return (
      <div className="debrief rc-root">
        <div className="rc-panel rc-thanks">
          <div className="rc-thanks-badge" aria-hidden="true">
            <svg viewBox="0 0 64 64"><circle cx="32" cy="32" r="28" /><path d="M20 33l8 8 16-17" /></svg>
          </div>
          <span className="rc-kicker">{tx("Course complete")}</span>
          <h2>{txr("Thank you for completing{br}{title}", { br: <br />, title })}</h2>
          <p className="rc-thanks-lead">
            {txr("You finished the course to the end. Your result has been recorded: quiz {correct} of {total}{v}", { correct, total, v: passed ? tx(", passed.") : tx(". Have another go whenever you like.") })}
          </p>
          <ul className="rc-thanks-facts">
            <li><b>{correct}/{total}</b><span>{tx("Quiz")}</span></li>
            <li><b>{passed ? tx("Passed") : tx("Practice")}</b><span>{tx("Result")}</span></li>
            <li><b>{new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</b><span>{tx("Completed")}</span></li>
          </ul>
          <p className="rc-thanks-note">{tx("You can close this window now.")}</p>
          <div className="rc-actions">
            <button type="button" className="rc-btn" onClick={() => setDone(false)}>{tx("Back to results")}</button>
            <button type="button" className="rc-btn rc-btn--ghost" onClick={() => window.location.reload()}>{tx("Restart course")}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="debrief rc-root">
      <div className="rc-panel">
        <header className="rc-head">
          <div className={`rc-ring ${passed ? "is-pass" : "is-fail"}`} style={{ ["--p" as string]: `${pct}` }}>
            <b>{correct}/{total}</b>
            <small>{tx("quiz")}</small>
          </div>
          <div className="rc-head-c">
            <span className="rc-kicker">{tx("Results")}</span>
            <h2>{title}</h2>
            <span className={`rc-verdict ${passed ? "is-pass" : "is-fail"}`}>{passed ? tx("Passed") : tx("Needs practice")}</span>
            <p>
              {passed
                ? tx("You spotted the key risks. The table shows each answer; open a row for the explanation.")
                : tx("Some answers missed the point. The wrong ones are highlighted below, open each to see why.")}
            </p>
          </div>
        </header>

        <div className="rc-table" role="table" aria-label={tx("Quiz review")}>
          {rows.map(({ q, i, chosen, ok, right }) => {
            const isOpen = open === q.id;
            return (
              <div key={q.id} className={`rc-row ${ok ? "is-ok" : "is-bad"}${isOpen ? " is-open" : ""}`} role="row">
                <button type="button" className="rc-row-h" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : q.id)}>
                  <span className="rc-n">{i + 1}</span>
                  <span className="rc-t">{q.title ?? q.prompt}</span>
                  <span className="rc-a">{chosen ? chosen.label : tx("Not answered")}</span>
                  <span className={`rc-s ${ok ? "is-ok" : "is-bad"}`}>{ok ? tx("✓ Correct") : tx("✗ Wrong")}</span>
                  <span className="rc-chev" aria-hidden="true">{isOpen ? "\u25B2" : "\u25BC"}</span>
                </button>
                {isOpen && (
                  <div className="rc-row-b">
                    <p className="rc-prompt">{q.prompt}</p>
                    <div className="rc-ans">
                      <span>{tx("Your answer")}</span>
                      <b className={ok ? "is-ok" : "is-bad"}>{chosen ? chosen.label : tx("Not answered")}</b>
                    </div>
                    {!ok && right && (
                      <div className="rc-ans">
                        <span>{tx("Correct answer")}</span>
                        <b className="is-ok">{right.label}</b>
                      </div>
                    )}
                    {q.rationale && <p className="rc-why" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(q.rationale) }} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {dims && (
          <section className="rc-exp">
            <div className="rc-exp-h">
              <span>{tx("What this run exposed to an attacker")}</span>
              {evidence.length > 0 && (
                <button type="button" className="rc-link" onClick={() => setShowEv((v) => !v)}>
                  {showEv ? tx("Hide details") : tx("Details ({length})", { length: evidence.length })}
                </button>
              )}
            </div>
            <div className="rc-exp-dims">
              {dims.map((d) => (
                <div key={d.dimension} className="rc-dim">
                  <span>{d.label}</span>
                  <i><em className={`rc-fill rc-fill--${d.dimension}`} style={{ width: `${d.percent}%` }} /></i>
                </div>
              ))}
            </div>
            {showEv && (
              <ul className="rc-ev">
                {evidence.slice().reverse().map((it) => (
                  <li key={`${it.id}-${it.at}`} className={it.kind === "risk" ? "is-bad" : "is-ok"}>
                    <span aria-hidden="true">{it.kind === "risk" ? "\u26A0" : "\u2713"}</span>
                    {it.label}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {choices && (
          <section className="rc-exp">
            <div className="rc-exp-h">
              <span>{tx("Your choices during this run")}</span>
              <button type="button" className="rc-link" onClick={() => setShowChoices((v) => !v)}>
                {showChoices ? tx("Hide details") : tx("Details ({count})", { count: choices.count })}
              </button>
            </div>
            {showChoices && (
              <div className="rc-choices">
                <p className="rc-note">{tx("Not part of your score: some choices were forced so you could see what a real attack looks like from the inside.")}</p>
                {choices.body}
              </div>
            )}
          </section>
        )}

        <footer className="rc-actions">
          <button type="button" className="rc-btn rc-btn--ghost" onClick={() => window.location.reload()}>{tx("Try again")}</button>
          <button type="button" className="rc-btn cf-pulse-target" onClick={() => setDone(true)}>{tx("Finish course")}</button>
        </footer>
      </div>
    </div>
  );
}
